/**
 * Full-dataset JSON export.
 *
 * Bundles every entity the app knows about — positions, moves,
 * counters, media refs, and config — into a single JSON file and
 * triggers a browser download. Intended for the curator to review /
 * clean the dataset offline: every node, its tags, its textual
 * description, and its linked video / media refs land in one file.
 *
 * Reads directly from `dataSource` (the same static-JSON adapter the
 * app boots from) so the export always reflects the shipped data,
 * independent of any runtime canvas filters. When a real DB provider
 * replaces the static source (ADR-006), this export keeps working
 * unchanged — it just pulls from whatever `dataSource` returns.
 *
 * The export is a plain snapshot, NOT a re-importable migration
 * format — it's for human review. If we later want round-trip
 * import, version the shape and add a loader.
 */
import { dataSource } from '../data/source';

export type DataExport = {
  /** ISO timestamp of when the export was generated. */
  exportedAt: string;
  /** Build stamp of the running app, for provenance. */
  build: { number: string; sha: string };
  counts: {
    positions: number;
    moves: number;
    counters: number;
  };
  positions: unknown[];
  moves: unknown[];
  counters: unknown[];
  /** Media refs index keyed by entity id (videos, images, etc.). */
  mediaRefs: unknown;
  config: unknown;
};

/**
 * Gather the full dataset into a single object. Exposed separately
 * from the download so a future "preview export" UI could render it
 * without forcing a file download.
 */
export async function buildDataExport(): Promise<DataExport> {
  const [positions, moves, counters, config, mediaRefs] = await Promise.all([
    dataSource.positions(),
    dataSource.moves(),
    dataSource.counters(),
    dataSource.config(),
    dataSource.mediaRefs(),
  ]);
  return {
    exportedAt: new Date().toISOString(),
    build: {
      number: __BJJMAP_BUILD_NUMBER__,
      sha: __BJJMAP_BUILD_SHA__,
    },
    counts: {
      positions: positions.length,
      moves: moves.length,
      counters: counters.length,
    },
    positions,
    moves,
    counters,
    mediaRefs,
    config,
  };
}

/**
 * Build the export and trigger a browser download as
 * `bjjmap-data-<date>.json`. Uses a Blob + object URL + synthetic
 * anchor click — the standard no-dependency download pattern.
 */
export async function downloadDataExport(): Promise<void> {
  const data = await buildDataExport();
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const a = document.createElement('a');
  a.href = url;
  a.download = `bjjmap-data-${date}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick so the click has time to start the
  // download before the URL is invalidated.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
